import * as Sentry from "@sentry/vue";
import { speckleGraphql } from "./speckleGraphql";

const STREAM_MODELS_QUERY = `
  query StreamModels($streamId: String!) {
    project(id: $streamId) {
      models(limit: 100) {
        items {
          id
          name
          versions(limit: 1) {
            totalCount
          }
        }
      }
    }
  }
`;

const DELETE_MODEL_MUTATION = `
  mutation DeleteStreamModel($streamId: ID!, $modelId: ID!) {
    modelMutations {
      delete(input: { projectId: $streamId, id: $modelId })
    }
  }
`;

export type SpeckleStreamModel = {
  id: string;
  name: string;
  /** Versions published to the model so far; 0 is an empty shell. */
  versionCount: number;
};

type StreamModelsData = {
  project?: { models?: { items?: Array<{ id: string; name: string; versions?: { totalCount?: number } }> } } | null;
};

/**
 * The models the Speckle server itself holds for a stream, read with the user's own Speckle token.
 * This is the other side of the project's model list: a model created from the connector or on
 * Speckle exists there before this platform knows about it, and a model typed here exists on the
 * platform's list the moment it is created. `null` when the answer is unknown (no token, no access,
 * server unreachable), so a caller can show nothing rather than something wrong.
 */
export const getSpeckleStreamModels = async (streamId: string): Promise<SpeckleStreamModel[] | null> => {
  if (!streamId) return null;
  try {
    const { data } = await speckleGraphql<StreamModelsData>(STREAM_MODELS_QUERY, { streamId });
    const items = data?.project?.models?.items;
    if (!items) return null;
    return items.map((item) => ({ id: item.id, name: item.name, versionCount: item.versions?.totalCount ?? 0 }));
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export const getSpeckleStreamModelNames = async (streamId: string): Promise<string[] | null> => {
  const models = await getSpeckleStreamModels(streamId);
  return models ? models.map((model) => model.name) : null;
};

export type DeleteEmptyModelOutcome = "deleted" | "notFound" | "hasVersions" | "failed";

/**
 * Delete a model from the stream on the Speckle server, only while nothing has been published to it.
 *
 * The version count is read from Speckle right before the delete rather than trusted from the
 * project's own list: a model pulled in from the stream carries no commit locally whatever Speckle
 * holds, and deleting a model deletes its versions with it. Deleting here changes the stream only;
 * the project's list catches up when the API re-reads the stream (UpdateBranch).
 */
export const deleteEmptySpeckleModel = async (streamId: string, modelName: string): Promise<DeleteEmptyModelOutcome> => {
  const models = await getSpeckleStreamModels(streamId);
  if (!models) return "failed";

  const model = models.find((candidate) => candidate.name.toLowerCase() === modelName.toLowerCase());
  if (!model) return "notFound";
  if (model.versionCount > 0) return "hasVersions";

  try {
    const { data, errors } = await speckleGraphql<{ modelMutations?: { delete?: boolean } }>(DELETE_MODEL_MUTATION, {
      streamId,
      modelId: model.id,
    });
    if (data?.modelMutations?.delete) return "deleted";
    Sentry.captureMessage(`Speckle refused to delete model "${modelName}" on ${streamId}: ${errors.join("; ")}`);
    return "failed";
  } catch (error) {
    Sentry.captureException(error);
    return "failed";
  }
};
